'use strict';

/**
 * T320 P4 (LD-5) — Tasks ding emitter (counters only, NO timers).
 *
 * Decides which T90 ding trigger(s) fire on a routine/round counter
 * increment. The triggers themselves are registered by tasks-dings-init.js
 * (required once at startup). Pure + side-effect-free so it is unit-testable
 * and the increment route can call it to emit a `tasks:ding` socket event.
 *
 * Trigger mapping:
 *   - a round reaching its targetCount  → 'tasks:round-timer-done'
 *     (reused as the round-complete chime — name is historical; this is a
 *     COUNTER target, not a timer)
 *   - the whole routine completing      → 'tasks:routine-completed'
 */

const ROUND_COMPLETE_TRIGGER = 'tasks:round-timer-done';
const ROUTINE_COMPLETE_TRIGGER = 'tasks:routine-completed';

/**
 * @param {object} params
 * @param {{type:string}} params.task — the incremented task (round or routine)
 * @param {boolean} params.completed — did THIS task hit its targetCount
 * @param {boolean} params.routineCompleted — did the parent routine fully complete
 * @returns {string[]} ordered trigger ids to fire (possibly empty)
 */
function dingTriggersForIncrement({ task, completed, routineCompleted } = {}) {
  const triggers = [];
  if (!task) return triggers;
  if (completed && (task.type === 'round' || task.type === 'routine')) {
    triggers.push(ROUND_COMPLETE_TRIGGER);
  }
  if (routineCompleted) {
    triggers.push(ROUTINE_COMPLETE_TRIGGER);
  }
  return triggers;
}

/**
 * Given a user task state and the id of a round/routine that was just
 * incremented, return true when its parent routine is now fully complete
 * (every child round isCompleted). For a top-level routine with no parent,
 * checks the routine's own children.
 *
 * @param {{tasks:object}} userState
 * @param {string} taskId
 * @returns {boolean}
 */
function routineCompletionState(userState, taskId) {
  if (!userState || !userState.tasks) return false;
  const task = userState.tasks[taskId];
  if (!task) return false;

  // Resolve the routine: the parent (if this is a round) or the task itself.
  let routine = null;
  if (task.parentTaskId && userState.tasks[task.parentTaskId]) {
    routine = userState.tasks[task.parentTaskId];
  } else if (task.type === 'routine') {
    routine = task;
  }
  if (!routine) return false;

  const childIds = Array.isArray(routine.childTaskIds) ? routine.childTaskIds : [];
  if (childIds.length === 0) return false;
  return childIds.every((cid) => {
    const child = userState.tasks[cid];
    return child && child.isCompleted === true;
  });
}

module.exports = {
  dingTriggersForIncrement,
  routineCompletionState,
  ROUND_COMPLETE_TRIGGER,
  ROUTINE_COMPLETE_TRIGGER,
};
